import type { Ref } from 'vue'
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import {
  AUDIO_RHYTHM_COLORS,
  AUDIO_RHYTHM_MODES,
  DEFAULT_AUDIO_RHYTHM_SETTINGS,
  SPECTRUM_COLUMN_COUNT,
  AudioRhythmEngine,
  buildVisualKeys,
  getBoundAuxLedMap,
  getLayoutMetrics,
} from '@rapoo/device-sdk'
import type {
  AudioRhythmColorId,
  AudioRhythmMode,
  AudioRhythmSettings,
  BaseDevice,
  DeviceLightSink,
  Rgb,
  VisualKey,
  DeviceManifest,
} from '@rapoo/device-sdk'
import { useDeviceStore } from '@/stores'
import { storeToRefs } from 'pinia'
import { SystemAudioCapture } from './audioCapture'
import type { AudioCaptureStatus } from './audioCapture'

/**
 * 音频律动组合式函数。
 * @param deviceRef 可选：外部设备引用（如 sdk-test 页的选中设备）。
 *   预览布局 / 引擎 / 下发必须基于同一台设备的 manifest，默认跟随全局 deviceStore；
 *   传入后改跟随外部引用，避免「预览键盘与下发设备不一致导致真机灯光错乱」。
 */
export function useAudioRhythm(deviceRef?: Ref<BaseDevice | null | undefined>) {
  // 按当前连接设备（VID/PID 匹配的 manifest）生成灯位映射，设备变化时自动重建。
  // 不再写死任何具体型号（此前硬编码 Ace-68-Turbo，导致 VT68 等设备灯光错位）。
  const deviceStore = useDeviceStore()
  const deviceSource = deviceRef ?? storeToRefs(deviceStore).currentDevice

  const keys = shallowRef<VisualKey[]>([])
  const layoutMetrics = shallowRef({ rowCount: 1, colCount: 0 })
  /** 主灯 → 副灯映射（如空格 11 → 72/74/76/78），副灯作为整体键跟随主灯。 */
  const boundAuxLedMap = shallowRef(new Map<number, number[]>())
  /** 灯效计算引擎（SDK 层）：频域数据 → ledIndex RGB 映射。 */
  const engine = shallowRef<AudioRhythmEngine | null>(null)

  function rebuildForDevice(device: { manifest?: DeviceManifest } | null | undefined) {
    const manifest = device?.manifest
    if (!manifest) return
    const vk = buildVisualKeys(manifest)
    keys.value = vk
    layoutMetrics.value = getLayoutMetrics(vk)
    const aux = getBoundAuxLedMap(manifest)
    boundAuxLedMap.value = aux
    engine.value = new AudioRhythmEngine({
      keys: vk,
      rowCount: layoutMetrics.value.rowCount,
      columnCount: SPECTRUM_COLUMN_COUNT,
      boundAuxLedMap: aux,
    })
  }

  watch(deviceSource, (device) => rebuildForDevice(device), { immediate: true })

  const capture = new SystemAudioCapture()
  const status = ref<AudioCaptureStatus>('idle')
  const statusMessage = ref('')
  const usingMic = ref(false)
  const mode = ref<AudioRhythmMode>(AUDIO_RHYTHM_MODES[0]!.id)
  const colorId = ref<AudioRhythmColorId>(AUDIO_RHYTHM_COLORS[0]!.id)
  const settings = ref<AudioRhythmSettings>({ ...DEFAULT_AUDIO_RHYTHM_SETTINGS })
  const running = ref(false)

  /** 当前帧：ledIndex → RGB（已含亮度与拖尾）。 */
  const frameColors = shallowRef<Map<number, Rgb>>(new Map())
  const bandsSnapshot = shallowRef({ low: 0, mid: 0, high: 0, overall: 0, beat: 0 })

  /** 设备下发开关；检测到音效信号时自动打开。 */
  const deviceSinkEnabled = ref(false)
  /** 是否检测到音效信号（驱动灯效与设备下发）。 */
  const hasAudio = ref(false)
  /** 下发链路诊断文本（UI 直接可见，定位「未下发」）。 */
  const sinkStatus = ref('未开始监听')
  let deviceSink: DeviceLightSink | null = null

  let rafId = 0
  let startedAt = 0
  /** 最近一次检测到音效信号的时刻（用于 UI 去抖显示）。 */
  let lastSignalAt = 0
  /** 预览节流：约 80ms 一帧，不阻塞 0xdd；真机干净后不必再因下发拉长预览间隔。 */
  let lastPreviewAt = 0
  const PREVIEW_INTERVAL_MS = 80
  /** 诊断日志节流：每 2s 最多打一条，定位「未下发」断点。 */
  let lastDiagAt = 0
  function diag(message: string): void {
    const now = performance.now()
    if (now - lastDiagAt > 2000) {
      lastDiagAt = now
      console.warn('[useAudioRhythm]', message)
    }
  }
  /** UI 诊断文本节流：每 500ms 更新一次。 */
  let lastStatusAt = 0
  /** 用户手动关闭过下发后，不再自动打开（尊重手动开关）。 */
  let userDisabled = false
  /** 程序性关闭（停止监听）标志：不标记为用户手动关闭。 */
  let programmaticStop = false
  watch(deviceSinkEnabled, (on, prev) => {
    if (programmaticStop) {
      programmaticStop = false
      return
    }
    if (!on) userDisabled = true
    else if (prev === false) userDisabled = false
  })

  const modes = AUDIO_RHYTHM_MODES
  const colors = AUDIO_RHYTHM_COLORS

  const statusLabel = computed(() => {
    if (status.value === 'active') return usingMic.value ? '麦克风' : '系统音频'
    if (status.value === 'requesting') return '请求中'
    if (status.value === 'error') return '音频错误'
    return '未连接'
  })

  const unsub = capture.onStatus((s, msg) => {
    status.value = s
    statusMessage.value = msg ?? ''
    usingMic.value = capture.isUsingMicFallback()
    if (s !== 'active' && running.value) {
      stopLoop()
    }
  })

  function setDeviceSink(sink: DeviceLightSink | null) {
    deviceSink = sink
  }

  async function startSystemAudio(options?: { startLoop?: boolean }) {
    await capture.startSystemAudio()
    // 用户主动开始监听 = 期望下发：解除手动关闭标记，恢复「检测到音效自动打开」
    userDisabled = false
    // 重新启用下发（若之前停止监听时禁用过 sink）
    if (deviceSink) deviceSink.enabled = true
    if (options?.startLoop === false) return
    startLoop()
  }

  async function startMicrophone() {
    await capture.startMicrophone()
    // 用户主动开始监听 = 期望下发：解除手动关闭标记，恢复「检测到音效自动打开」
    userDisabled = false
    // 重新启用下发（若之前停止监听时禁用过 sink）
    if (deviceSink) deviceSink.enabled = true
    startLoop()
  }

  function stopAudio() {
    stopLoop()
    capture.stop()
    frameColors.value = new Map()
    engine.value?.reset()
    // 停止监听：取消下发勾选（触发恢复内置灯效），但不禁用 sink 本身，
    // 否则同一设备重新开始监听后 sink.enabled 永久为 false 导致不再下发。
    // 监听运行期间关闭音乐不会走到这里（仍持续下发黑帧保持黑屏）。
    if (deviceSinkEnabled.value) {
      programmaticStop = true
      deviceSinkEnabled.value = false
    }
    // 退出快速通讯并恢复内置灯效，避免灯光页后续 GetFunc 再被 50ms 超时误判断连
    if (deviceSink) {
      deviceSink.enabled = false
    }
  }

  function startLoop() {
    if (running.value) return
    running.value = true
    startedAt = performance.now()
    const tick = () => {
      if (!running.value) return
      renderFrame()
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
  }

  function stopLoop() {
    running.value = false
    if (rafId) {
      cancelAnimationFrame(rafId)
      rafId = 0
    }
  }

  function renderFrame() {
    const data = capture.getFrequencyData()
    if (!data || !engine.value) return

    const timeSec = (performance.now() - startedAt) / 1000
    // 灯效计算（频域数据 → ledIndex RGB 映射 + 频段快照 + 信号判定）全部由 SDK 引擎完成
    const {
      frame: bright,
      bands,
      hasSignal,
    } = engine.value.renderFrame(
      data,
      capture.getSampleRate(),
      mode.value,
      colorId.value,
      settings.value,
      timeSec,
    )

    const nowMs = performance.now()
    if (hasSignal) lastSignalAt = nowMs

    // 获取到音效后自动打开设备下发（用户手动关闭过则不再自动打开）；
    // 无信号时保持已开状态，避免反复开关
    if (hasSignal && deviceSink && !deviceSinkEnabled.value && !userDisabled) {
      deviceSinkEnabled.value = true
    }

    // 真机推帧 fire-and-forget：await 会把引擎锁在 HID 上，全部灯效看起来卡死。
    const sinkEnabled = deviceSinkEnabled.value && deviceSink?.enabled
    if (performance.now() - lastStatusAt > 500) {
      lastStatusAt = performance.now()
      const stats = deviceSink?.getStats?.()
      sinkStatus.value = `下发开关=${deviceSinkEnabled.value} · 设备=${deviceSink ? '有' : '无'} · sink=${deviceSink ? deviceSink.enabled : '-'} · 信号=${hasSignal} · 推帧=${sinkEnabled ? '进行中' : '未推送'} · 帧率=${stats ? stats.fps : '-'}fps · 每帧${stats ? stats.avgSegments : '-'}段`
    }
    if (!sinkEnabled) {
      diag(
        `监听中未下发: deviceSinkEnabled=${deviceSinkEnabled.value} deviceSink=${deviceSink ? '有' : 'null'} sink.enabled=${deviceSink ? deviceSink.enabled : '-'} hasSignal=${hasSignal}`,
      )
    }
    if (sinkEnabled && deviceSink) {
      // 0xdd 是「逐段下发、逐段刷新」，段序应与效果的视觉运动方向一致，才能把
      // 分段刷新的先后顺序掩盖成自然的扩散/流动，而不是「从左往右刷屏」：
      //  - 中心聚集效果（中央脉冲/呼吸）：centerFirst（先亮中段、向两侧扩散）；
      //  - 全盘/移动效果（扫描线/波浪等）：linear（顺地址方向）。
      const order =
        mode.value === 'centralPulse' || mode.value === 'beatBreathe' ? 'centerFirst' : 'linear'
      void Promise.resolve(deviceSink.pushFrame(bright, order)).catch(() => undefined)
    }

    if (nowMs - lastPreviewAt >= PREVIEW_INTERVAL_MS) {
      lastPreviewAt = nowMs
      bandsSnapshot.value = {
        low: bands.low,
        mid: bands.mid,
        high: bands.high,
        overall: bands.overall,
        beat: bands.beat,
      }
      frameColors.value = bright
      hasAudio.value = nowMs - lastSignalAt < 400
    }
  }

  onBeforeUnmount(() => {
    stopAudio()
    unsub()
  })

  return {
    modes,
    colors,
    keys,
    layoutMetrics,
    mode,
    colorId,
    settings,
    status,
    statusLabel,
    statusMessage,
    usingMic,
    running,
    frameColors,
    bandsSnapshot,
    deviceSinkEnabled,
    hasAudio,
    sinkStatus,
    setDeviceSink,
    startSystemAudio,
    startMicrophone,
    stopAudio,
    startLoop,
  }
}
